import React from "react";
import Modal from "react-modal";
import { updateAttributesOnDocument } from "util/pouchActions";
import { Lifter, LifterDivision, Meet } from "types";
import Select from "components/select/Select";
import {
  getAwardDivisionOptions,
  getFlightOptions,
  getPlatformOptions,
  getSessionOptions,
} from "util/options";
import size from "lodash/size";
import { getDivisionDoc } from "util/lifterHelper";
import first from "lodash/first";

const initialSelections: {
  platformId: string | null;
  session: number | null;
  flight: string | null;
  divisionId: string | null;
} = {
  platformId: null,
  session: null,
  flight: null,
  divisionId: null,
};

const BulkEditModal = ({
  meet,
  onRequestClose,
  isOpen,
  filteredLifters,
  activeFilters,
  filters,
}: {
  meet: Meet;
  onRequestClose: () => void;
  isOpen: boolean;
  filteredLifters: Lifter[];
  activeFilters: boolean;
  filters: {
    gender: string | null;
    rawOrEquipped: string | null;
  };
}) => {
  const [selections, setSelections] = React.useState(initialSelections);
  const [loading, setLoading] = React.useState(false);

  const close = () => {
    setSelections(initialSelections);
    setLoading(false);
    onRequestClose();
  };

  const updateLiters = () => {
    setLoading(true);

    if (
      selections.divisionId ||
      selections.flight ||
      selections.platformId ||
      selections.session
    ) {
      filteredLifters.forEach((lifter) => {
        const updatedData: Partial<Lifter> = {};
        if (selections.platformId) {
          updatedData.platformId = selections.platformId;
        }
        if (selections.session) {
          updatedData.session = selections.session;
        }
        if (selections.flight) {
          updatedData.flight = selections.flight;
        }
        if (selections.divisionId) {
          const hasExisting = lifter.divisions.some(
            (d) => d.divisionId === selections.divisionId,
          );
          if (!hasExisting) {
            const divisionDoc = getDivisionDoc(selections.divisionId, meet);
            if (divisionDoc) {
              const lifterDivision: LifterDivision = {
                divisionId: selections.divisionId,
                rawOrEquipped: filters.rawOrEquipped,
                declaredAwardsWeightClassId: first(
                  Object.keys(divisionDoc?.weightClasses),
                ),
              };
              updatedData.divisions = [...lifter.divisions, lifterDivision];
            }
          }
        }
        if (size(updatedData)) {
          updateAttributesOnDocument(meet._id, lifter._id, updatedData);
        }
      });
      // give time for data to loop back into the UI from pouchdb
      // usually we use a full page loader but that would wipe out the filters.
      setTimeout(() => close(), size(filteredLifters) * 150);
    } else {
      close();
    }
  };

  const onChangeSelectFilter = (
    name: keyof typeof initialSelections,
    value: string | number | undefined | null,
  ) => {
    setSelections({ ...selections, [name]: value });
  };

  const platformOptions = getPlatformOptions({}, meet);

  return (
    <Modal
      ariaHideApp={false}
      isOpen={isOpen}
      contentLabel="Bulk Edit Lifters"
      onRequestClose={onRequestClose}
      className={{
        base: "generate-lot-numbers-modal",
        afterOpen: "generate-lot-numbers-modal-after-open",
        beforeClose: "generate-lot-numbers-modal-before-close",
      }}
      overlayClassName={{
        base: "modal-overlay",
        afterOpen: "",
        beforeClose: "",
      }}
    >
      <div className="content">
        <h2>
          Bulk Edit {filteredLifters.length}{" "}
          {activeFilters ? "Filtered" : "(ALL)"} Lifters
        </h2>

        <div className="bulk-edit-row">
          Platform:
          <Select
            name="platformId"
            value={selections.platformId}
            options={platformOptions}
            blankValueLabel="NO CHANGE"
            onChange={(value) => onChangeSelectFilter("platformId", value)}
          />
        </div>
        <div className="bulk-edit-row">
          Session:
          <Select
            name="session"
            value={selections.session}
            options={getSessionOptions()}
            blankValueLabel="NO CHANGE"
            onChange={(value) => onChangeSelectFilter("session", value)}
          />
        </div>
        <div className="bulk-edit-row">
          Flight:
          <Select
            name="flight"
            value={selections.flight}
            options={getFlightOptions()}
            blankValueLabel="NO CHANGE"
            onChange={(value) => onChangeSelectFilter("flight", value)}
          />
        </div>

        <div className="bulk-edit-row">
          Add Division:
          {!filters.gender || !filters.rawOrEquipped ? (
            <div>Must filter by Gender and Equipment Level</div>
          ) : (
            <Select
              disabled={!filters.gender || !filters.rawOrEquipped}
              name="divisionId"
              value={selections.divisionId}
              options={getAwardDivisionOptions(
                {
                  _id: "fake",
                  gender: filters.gender,
                  divisions: [{ rawOrEquipped: filters.rawOrEquipped }],
                },
                meet,
                0,
              )}
              blankValueLabel="NO CHANGE"
              onChange={(value) => onChangeSelectFilter("divisionId", value)}
            />
          )}
        </div>

        <p>
          WARNING: THIS WILL OVERWRITE DATA FOR{" "}
          {activeFilters ? "THE CURRENTLY FILTERED LIFTERS" : "ALL LIFTERS"}.{" "}
          {filteredLifters.length} WILL BE UPDATED. THIS CANNOT BE UNDONE.
        </p>
      </div>
      <div className="button-row">
        <button disabled={loading} onClick={close} style={{ marginRight: 12 }}>
          Cancel
        </button>
        <button disabled={loading} onClick={updateLiters}>
          {loading ? "Saving..." : "Save"}
        </button>
      </div>
    </Modal>
  );
};

export default BulkEditModal;
